import { Alert, Button, Form, Input, InputNumber, Modal, Table } from 'antd';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge } from '../../components/StatusBadge';
import { EmptyState } from '../../components/EmptyState';
import { ApiError } from '../../lib/http';
import { AuditOutlined, ExclamationCircleOutlined, FormOutlined } from '../../icons';
import { jobsApi, requestsApi, reservationsApi, statsApi } from '../../services/resources';
import type { ActivityCounts, ExperimentRequest, JobSummary, Reservation } from '../../types/api';

export function AdminHomePage() {
  const [pendingR, setPendingR] = useState<Reservation[]>([]);
  const [pendingQ, setPendingQ] = useState<ExperimentRequest[]>([]);
  const [overdue, setOverdue] = useState(0);
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [counts, setCounts] = useState<ActivityCounts | null>(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  async function load() {
    try {
      const [r, q, o, j, c] = await Promise.all([
        reservationsApi.list({ status: 'pending', page: 1, limit: 20 }),
        requestsApi.list({ status: 'pending', page: 1, limit: 20 }),
        requestsApi.list({ sla_overdue: true, page: 1, limit: 1 }),
        jobsApi.list(),
        statsApi.counts().catch(() => null),
      ]);
      setPendingR(r.items);
      setPendingQ(q.items);
      setOverdue(o.total);
      setJobs(j);
      setCounts(c);
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? `列表加载失败：${e.message}` : '列表加载失败，请检查网络后重试');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="stack">
      <h1 className="page-title">工作台</h1>
      {overdue > 0 ? (
        <Alert type="warning" showIcon message={`超期未审学生申请 ${overdue} 条，请在 2 个工作日内处理。`} />
      ) : null}
      <div className="counts">
        <div className="count-card">
          <AuditOutlined /> 待审预约
          <strong>{pendingR.length}</strong>
        </div>
        <div className="count-card">
          <FormOutlined /> 待审申请
          <strong>{pendingQ.length}</strong>
        </div>
        <div className="count-card">
          <ExclamationCircleOutlined /> 今日冲突
          <strong>
            <Link to="/admin/labs">查看日历</Link>
          </strong>
        </div>
      </div>
      {error ? (
        <EmptyState text={error} action={{ label: '重新加载', onClick: () => void load() }} />
      ) : pendingR.length + pendingQ.length === 0 ? (
        <EmptyState text="当前没有待审单。新的教师预约会出现在这里。" />
      ) : (
        <Table
          scroll={{ x: 760 }}
          rowKey="id"
          pagination={false}
          dataSource={[
            ...pendingR.map((x) => ({ ...x, kind: '预约' })),
            ...pendingQ.map((x) => ({ ...x, kind: '申请', remark: x.title })),
          ]}
          columns={[
            { title: '类型', dataIndex: 'kind', width: 80 },
            { title: '申请人', dataIndex: 'user_name' },
            { title: '事由', dataIndex: 'remark' },
            {
              title: '状态',
              render: (_, row) => <StatusBadge status={row.status} reasonType={row.reason_type} />,
            },
            {
              title: '操作',
              render: (_, row) => (
                <Link to={row.kind === '预约' ? '/admin/reservations' : '/admin/requests'}>去处理</Link>
              ),
            },
          ]}
        />
      )}
      <section className="surface" style={{ padding: 16 }}>
        <h2 className="page-title">本周占用缩略</h2>
        <Link to="/admin/labs">打开冲突日历</Link>
      </section>
      {jobs.length > 0 ? (
        <p className="meta">后台任务：{jobs.map((j) => `${j.name ?? 'job'} ${j.status ?? ''}`).join('；')}</p>
      ) : null}
      {counts ? (
        <p className="meta">
          开展次数（系统去重）{counts.corrected_count}
          <Button type="link" onClick={() => setOpen(true)}>
            校正
          </Button>
        </p>
      ) : null}
      <CorrectModal open={open} counts={counts} onClose={() => setOpen(false)} onOk={() => void load()} />
    </div>
  );
}

function CorrectModal(props: {
  open: boolean;
  counts: ActivityCounts | null;
  onClose: () => void;
  onOk: () => void;
}) {
  const [to, setTo] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  return (
    <Modal
      open={props.open}
      title="校正开展次数"
      onCancel={props.onClose}
      onOk={async () => {
        if (to == null || !reason.trim() || !props.counts) return;
        await statsApi.correct({ from_value: props.counts.corrected_count, to_value: to, reason });
        props.onOk();
        props.onClose();
      }}
      okButtonProps={{ disabled: to == null || !reason.trim() }}
    >
      <p>将写入操作人、时间、原值、新值与原因。</p>
      <Form layout="vertical">
        <Form.Item label="新值">
          <InputNumber min={0} value={to ?? undefined} onChange={(v) => setTo(v)} />
        </Form.Item>
        <Form.Item label="原因" required>
          <Input.TextArea value={reason} onChange={(e) => setReason(e.target.value)} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
